import GardenRepository from "@/repositories/gardenRepository";
import type { SunAxis } from "@/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useAuthStore } from "./auth";

interface Garden {
  id: number;
  name: string;
  cell_size: number;
  // Compass bearing (degrees) the TOP of the plan points to: 0 = top points
  // North, 90 = East… Optional: older gardens default to 0 (top = North).
  north_angle?: number;
  // Read-only shared time axis for the sun[] arrays; `{}` before the first save.
  sun_axis?: SunAxis;
  [key: string]: unknown;
}

const norm = (deg: number) => ((deg % 360) + 360) % 360;
// The persisted `north_angle` (bearing the top points to) and the on-screen North
// needle angle (clockwise from the top) are mirror images: each is (360 − other).
const mirror = (deg: number) => (360 - norm(deg)) % 360;

export const useGardenStore = defineStore("garden", () => {
  const isGardenLoaded = ref(false);
  const garden = ref<Garden | null>(null);

  // `cell_size` is stored in decimetres. Single source for the two conversions
  // used all over the plan UI, with a 1 m fallback when no garden is loaded.
  const cellSizeMeters = computed(() => (garden.value?.cell_size ?? 10) / 10);
  const cellAreaM2 = computed(() => cellSizeMeters.value * cellSizeMeters.value);
  const cellsPerM2 = computed(() => 1 / cellAreaM2.value);

  // Surface (m²) of a given number of cells — the single place the count × cell-area
  // product lives (hover labels, space info, brush palette all read through it).
  const areaM2 = (cellCount: number) => cellCount * cellAreaM2.value;

  // On-screen angle of the North needle (degrees clockwise from the top of the
  // plan): 0 = needle up, 90 = right… This is what the compass UI manipulates; it
  // is the mirror of the persisted `north_angle`. The UI thinks "where is North on
  // screen"; the backend stores/uses `north_angle` for the solar azimuth.
  const orientation = computed(() => mirror(garden.value?.north_angle ?? 0));
  // Persisted value (backend convention), for the update payload.
  const northAngle = computed(() => norm(garden.value?.north_angle ?? 0));

  // Read-only sun time-axis per season; `{}` until the plan has been saved once.
  const sunAxis = computed<SunAxis>(() => garden.value?.sun_axis ?? {});

  async function getGardens(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    const { data }: { data: Garden[] } = await GardenRepository.getAll();
    // NB: Change this to be able to choose a garden
    garden.value = data[0] ?? null;
    isGardenLoaded.value = Boolean(data[0]);
  }

  function resetGardens() {
    isGardenLoaded.value = false;
    garden.value = null;
  }

  // Persist a new cell size (given in meters), stored back in decimetres.
  function setCellSize(meters: number) {
    if (!garden.value) return;
    garden.value.cell_size = meters * 10;
  }

  // Set the North needle's on-screen angle (degrees); stored as its mirror,
  // `north_angle` (the bearing the top of the plan points to).
  function setOrientation(screenDegrees: number) {
    if (!garden.value) return;
    garden.value.north_angle = mirror(screenDegrees);
  }

  // Persist the current cell size immediately (partial PATCH). Called when the
  // Échelle slider is released.
  async function saveCellSize() {
    if (!garden.value) return;
    await GardenRepository.patch(garden.value.id, { cell_size: garden.value.cell_size });
  }

  // Persist the current orientation immediately (partial PATCH). The backend
  // recomputes sun-exposure; refresh the garden so `sun_axis` reflects it (the plan
  // reloads its cells separately for the per-cell exposure).
  async function saveOrientation() {
    if (!garden.value) return;
    await GardenRepository.patch(garden.value.id, { north_angle: northAngle.value });
    await getGardens();
  }

  return {
    isGardenLoaded,
    garden,
    cellSizeMeters,
    cellAreaM2,
    cellsPerM2,
    areaM2,
    orientation,
    northAngle,
    sunAxis,
    getGardens,
    resetGardens,
    setCellSize,
    setOrientation,
    saveCellSize,
    saveOrientation,
  };
});
